// TwilioCostTile.js — proof-of-life Twilio cost tile (Phase 2).
// Shows 7-day + 30-day totals from twilio_usage_daily (uid="system").
// Chart + by-direction / by-leg breakdowns ship in Phase 3 (>=7 days of data
// required for meaningful rendering — earliest 2026-04-30).
//
// Data source: twilio_usage_daily via useTwilioDailySystemSeries.

import React from 'react';
import { Card, CardContent, Typography, Box } from '@mui/material';
import { useTwilioDailySystemSeries, useTwilioTodayCost } from '../../../hooks/useAdminMetrics';

const formatUSD = (n) => `$${Number(n ?? 0).toFixed(4)}`;

const TwilioCostTile = () => {
  const { series, totals, loading, error } = useTwilioDailySystemSeries(30);
  const { today, loading: todayLoading } = useTwilioTodayCost();

  if (error) {
    return (
      <Card><CardContent>
        <Typography color="error">Twilio cost unavailable: {error.message}</Typography>
      </CardContent></Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>Twilio Cost</Typography>
        <Box sx={{ display: 'flex', gap: 4, mb: 1 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Today (so far)</Typography>
            <Typography variant="h4">{todayLoading ? '…' : formatUSD(today.cost_usd)}</Typography>
            <Typography variant="caption" color="text.secondary">· {todayLoading ? '…' : today.calls} legs</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Last 7 days</Typography>
            <Typography variant="h4">{loading ? '…' : formatUSD(totals.last7)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Last 30 days</Typography>
            <Typography variant="h4">{loading ? '…' : formatUSD(totals.last30)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Legs (7d)</Typography>
            <Typography variant="h4">{loading ? '…' : totals.legs7}</Typography>
          </Box>
        </Box>
        {!loading && series.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No data yet. Pipeline active since 2026-04-23; first automated rollup 2026-04-24 00:35 UTC.
          </Typography>
        )}
        {!loading && series.length > 0 && series.length < 7 && (
          <Typography variant="body2" color="text.secondary">
            {series.length} day(s) of data. Trend chart + breakdown tiles ship in Phase 3 once &gt;=7 days accumulate.
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          Total spend on Twilio voice infrastructure. One leg = one billable call segment (inbound, outbound, or transfer).
        </Typography>
      </CardContent>
    </Card>
  );
};

export default TwilioCostTile;
